import React, { useContext } from 'react';
import { useLocation, Outlet } from 'react-router-dom';
import styled from 'styled-components';
import { isEmpty } from 'lodash';

import Headline2 from '@joinforma/ui/components/Headline2';
import Subtitle2 from '@joinforma/ui/components/Title2';
import Body2 from '@joinforma/ui/components/Body2';
import TabBar from '@joinforma/ui/components/TabBar';
import TabItem from '@joinforma/ui/components/TabItem';
import { ReactComponent as ExclamationMarkSvg } from '@joinforma/ui/assets/icons/exclamation-mark.svg';
import SvgIcon from '@joinforma/ui/components/SvgIcon';
import { SvgIconSize } from '@joinforma/ui/constants/properties';
import useSnackbar from '@joinforma/ui/hooks/useSnackbar';

import { SettingsRoutes } from 'utils/routeGenerator';

import { SETTINGS_PAYMENT_PAGE_ELEMENTS } from 'constants/testSelectors';

import CurrentUserContext from 'contexts/CurrentUserContext';
import FeatureFlagContext from 'contexts/FeatureFlagContext';
import { FeatureFlag } from 'constants/featureFlag';
import { ConnectedAccountStatus } from 'constants/stripe';
import useQueryConnectedAccounts from 'hooks/useQueryConnectedAccounts';
import PageSkeleton from 'components/common/PageSkeleton';

import { Company, ConnectedAccount, Employee } from './types';

const Container = styled.div``;

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const MainHeading = styled(Headline2)``;

const SubText = styled(Body2)`
  display: block;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const TabContainer = styled.div`
  margin-top: 30px;
`;

const StyledTabBar = styled(TabBar)``;

const DirectDepositRequiredBanner = styled.div`
  display: flex;
  padding: 20px;
  background: #ffe5ca;
  border-radius: 7px;
  margin-top: 30px;
`;

const ExclamationMarkIcon = styled(SvgIcon).attrs({
  icon: ExclamationMarkSvg,
  size: SvgIconSize.MEDIUM,
})`
  margin-right: 17px;
`;

const LinkBankBannerTitle = styled(Subtitle2)``;
const SettingsLinkAccountsLayout = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const { company, employee }: { company: Company; employee: Employee } =
    useContext(CurrentUserContext);
  const { showErrorSnackbar } = useSnackbar();

  const { settings } = employee;
  const { external_payment } = settings;

  const { connected_account: stripeConnectedAccount } = external_payment;

  const featureFlag = useContext(FeatureFlagContext);

  const location = useLocation();

  const { marketplace, credit_card } = company.features;

  const migratedToPretaxV2: boolean = featureFlag.flag(
    FeatureFlag.ALEGEUS_MIGRATED_CUSTOMER
  );

  const linkBankBanner: boolean = featureFlag.flag(
    FeatureFlag.LINK_BANK_ACCOUNT_SETTINGS_BANNER
  );

  const needPretaxV2DDSetup =
    migratedToPretaxV2 &&
    external_payment.direct_deposit_account?.status === 'inactive';

  const unitOrStripe: string = featureFlag.flag(FeatureFlag.UNIT_OR_STRIPE);
  const isUnitEnabled = unitOrStripe === 'unit';

  const { data: connectedAccountsData } = useQueryConnectedAccounts({
    status: [
      ConnectedAccountStatus.ACTIVE,
      ConnectedAccountStatus.PENDING_VERIFICATION,
    ],
    enabled: isUnitEnabled,
    onError: () => {
      showErrorSnackbar({
        text: 'An error occurred while fetching your bank account. Please try again.',
      });
    },
  });

  const unitConnectedAccounts: Array<ConnectedAccount> = connectedAccountsData
    ? connectedAccountsData.connected_accounts
    : [];
  const primaryUnitConnectedAccount = unitConnectedAccounts.find(
    (c) => c.isPrimary
  );

  const connectedAccount = isUnitEnabled
    ? primaryUnitConnectedAccount
      ? {
          id: primaryUnitConnectedAccount.id,
          last4: primaryUnitConnectedAccount.accountLastFour,
          bank_account_name: primaryUnitConnectedAccount.name,
          status: primaryUnitConnectedAccount.status,
        }
      : undefined
    : stripeConnectedAccount;

  const setupDD =
    isEmpty(connectedAccount) ||
    (!isUnitEnabled &&
      stripeConnectedAccount?.status === ConnectedAccountStatus.DELETED);

  if (!company) {
    return <PageSkeleton />;
  }

  return (
    <Container data-testid={SETTINGS_PAYMENT_PAGE_ELEMENTS.PAYMENT_CONTAINER}>
      <MainContainer>
        <MainHeading>Linked accounts</MainHeading>
        <SubText>Manage your reimbursement and backup payment info.</SubText>
        {setupDD && needPretaxV2DDSetup && linkBankBanner && (
          <DirectDepositRequiredBanner>
            <ExclamationMarkIcon />
            <div>
              <LinkBankBannerTitle>Link a bank account</LinkBankBannerTitle>
              <div>
                <Body2>
                  Get reimbursed via direct deposit within 2-3 business days.
                  You won’t get reimbursed until you link a bank account.
                </Body2>
              </div>
            </div>
          </DirectDepositRequiredBanner>
        )}
        <TabContainer>
          <StyledTabBar>
            <TabItem
              active={location.pathname.includes('bank-accounts')}
              to={SettingsRoutes.bankAccounts()}
            >
              Reimbursement
            </TabItem>
            {(marketplace || credit_card) && (
              <TabItem
                active={location.pathname.includes('payment')}
                to={SettingsRoutes.payment()}
              >
                Payment
              </TabItem>
            )}
          </StyledTabBar>
        </TabContainer>
        {children ?? <Outlet />}
      </MainContainer>
    </Container>
  );
};

export default SettingsLinkAccountsLayout;
